// Pure helper used by PurchaseOrderCreate.vue's tableSelection setter.
//
// Vuetify 2's <v-data-table> with show-select + v-model emits "every selected
// item on the current page," not "every selected item ever." In server-side
// mode, blindly replacing the selection array would drop off-page selections
// each time the page or filter changes — which is the bug this function fixes.
//
// Rule: only mutate keys that are in `visibleIds`. Keys outside `visibleIds`
// represent off-page (or filtered-out) selections and must be preserved.
// Existing keys are NOT overwritten; the stored reference is kept so in-place
// edits on row objects (qty, unit cost) survive.
//
// Key normalization: Shopify-backed rows deliver `variant_id` as a STRING,
// not a number. The Map is keyed by `Number(variant_id)` here so every
// caller (including the component's getter/setter and the fetchData merge
// rule, all of which use `Number(p.variant_id)` for lookup) sees consistent
// keys. Non-numeric / NaN / null variant_ids are dropped from the Map.
export function applyTableSelectionDiff<T extends { variant_id: number | string }>(
  map: Map<number, T>,
  next: T[],
  visibleIds: Set<number>,
): void {
  const nextIds = new Set(
    next
      .map((item) => Number(item.variant_id))
      .filter((n) => Number.isFinite(n)),
  );

  // Remove: visible items that the user just unchecked on this page.
  for (const id of visibleIds) {
    if (!nextIds.has(id)) { map.delete(id); }
  }

  // Add: items the user just checked, keeping existing references stable.
  for (const item of next) {
    const key = Number(item.variant_id);
    if (!Number.isFinite(key)) { continue; }
    if (!map.has(key)) { map.set(key, item); }
  }
}
